var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import React from 'react';
import { push } from 'connected-react-router';
import { store } from 'store/store';
import StudentsStartConversationModal from 'components/organisms/students/modal_contents/start_conversation';
import NotifyMatching from 'components/organisms/teachers/modal_contents/notify_matching';
import TeachersStartConversationModal from 'components/organisms/teachers/modal_contents/start_conversation';
import { localStorage as localStorageConstants } from 'constants/index';
import * as ClientLogsActions from 'reducers/client_logs';
import * as ConversationActions from 'reducers/conversation';
import * as EnvironmentsActions from 'reducers/environments';
import * as ModalActions from 'reducers/modal';
import * as TeachersActions from 'reducers/teachers';
import * as checkUrl from 'utils/checkUrl';
import exponentialBackoff from 'utils/exponentialBackoff';
import getAuthToken from 'utils/getAuthToken';
import resolvePath from 'utils/resolvePath';
import wait from 'utils/wait';
import zeroPadding from './zeroPadding';
import dateTime from 'utils/dateTime';
import hasEvaluationCheck from 'utils/hasEvaluationCheck';
import { updateMailAuthenticationSuccess } from 'reducers/students';
import { DateTime } from 'luxon';
const isTeachers = () => window.location.pathname.includes('teachers');
const isStudents = () => window.location.pathname.includes('students');
const getEndpoint = () => __awaiter(void 0, void 0, void 0, function* () {
    try {
        yield store.dispatch(EnvironmentsActions.getEnvironments());
        return store.getState().rootReducer.environments.environments.app_socket_url;
    }
    catch (error) {
        throw error;
    }
});
let socket;
const retryDelay = 5;
export const connect = (WebSocketToken_1, ...args_1) => __awaiter(void 0, [WebSocketToken_1, ...args_1], void 0, function* (WebSocketToken, attempt = 1, delay = 5) {
    const endpoint = yield getEndpoint();
    if (socket || !endpoint) {
        return;
    }
    socket = new WebSocket(endpoint + WebSocketToken);
    listenCloseEvent();
    listenErrorEvent();
    socket.addEventListener('error', () => __awaiter(void 0, void 0, void 0, function* () {
        yield wait(exponentialBackoff(attempt, delay));
        connect(WebSocketToken, attempt + 1);
    }));
    listenOpenEvent();
    listenMessageEvent();
});
export const reconnect = () => {
    if (socket) {
        socket.close();
        socket = undefined;
    }
    const webSocketToken = localStorage.getItem(localStorageConstants.WEB_SOCKET_TOKEN_KEY);
    if (webSocketToken) {
        connect(webSocketToken).catch(() => {
            setTimeout(reconnect, retryDelay * 1000);
        });
    }
};
const send = (data_1, ...args_2) => __awaiter(void 0, [data_1, ...args_2], void 0, function* (data, attempt = 1, delay = 1.5) {
    if (!socket || socket.readyState !== 1) {
        yield wait(exponentialBackoff(attempt, delay));
        send(data, attempt + 1);
        return;
    }
    socket.send(JSON.stringify(data));
});
const subscribeChannel = (channel, subscribe = true) => {
    const data = {
        command: subscribe ? 'subscribe' : 'unsubscribe',
        identifier: `{"channel":"${channel}Channel"}`,
    };
    send(data);
};
const listenErrorEvent = () => {
    if (!socket) {
        return;
    }
    socket.addEventListener('error', (error) => __awaiter(void 0, void 0, void 0, function* () {
        const authToken = getAuthToken();
        store.dispatch(ClientLogsActions.report('websocket.error', error, authToken));
    }));
};
const listenCloseEvent = () => {
    if (!socket) {
        return;
    }
    socket.addEventListener('close', (error) => __awaiter(void 0, void 0, void 0, function* () {
        const authToken = getAuthToken();
        store.dispatch(ClientLogsActions.report('websocket.close', error, authToken));
        reconnect();
    }));
};
const listenOpenEvent = () => {
    if (!socket) {
        return;
    }
    socket.addEventListener('open', (error) => {
        subscribeChannel('Appearance');
        subscribeChannel('Conversations');
        const authToken = getAuthToken();
        store.dispatch(ClientLogsActions.report('websocket.open', error, authToken));
    });
};
const listenMessageEvent = () => {
    if (!socket) {
        return;
    }
    socket.addEventListener('message', (event) => {
        if (checkUrl.isSignedOutPage()) {
            return;
        }
        const data = JSON.parse(event.data);
        if (!data.message) {
            return;
        }
        const message = data.message;
        const channel = data.message.channel;
        if (data.type === 'ping') {
            window.serverTime = DateTime.fromSeconds(Number(message));
        }
        if (channel === 'ConversationsChannel') {
            conversationsChannelAction(message);
        }
        if (!!message.confirmed) {
            // メール認証が成功した場合サーバーから{confirmed: true}をもらい、
            // メール認証要求モーダルが消えるようにユーザーの情報を更新する。
            updateMailAuthentication();
        }
    });
};
const conversationsChannelAction = (message) => {
    const action = message.action;
    if (action === 'open') {
        markConversationOpen();
        openModalToStartConversation(message.conversation);
    }
    else if (action === 'close') {
        moveToEvaluation();
    }
    else if (action === 'late') {
        showLateBalloon();
    }
    else if (action === 'change_user') {
        changeUser(message.user.id);
    }
    else if (action === 'notify_matching') {
        openModalToNotifyMatching(message.conversation);
    }
    else if (action === 'remaining_time') {
        adjustRemainingTime(message);
    }
    else if (action === 'remote_online') {
        updateRemoteOnlineStatus(message.online);
        updateRemoteNetworkQuality(message.networkQuality);
    }
};
const adjustRemainingTime = (serverTime) => {
    store.dispatch(ConversationActions.setServerTime(serverTime));
    const current = dateTime.parseUnix(serverTime.current_time_epoch);
    const endAt = dateTime.parseUnix(serverTime.end_at_epoch);
    const startAt = dateTime.parseUnix(serverTime.start_at_epoch);
    const isBeforeStart = current < startAt;
    const remainingTime = endAt.diff(current, ['seconds']).toObject().seconds || 0;
    if (isBeforeStart) {
        store.dispatch(ConversationActions.setIsConversationStarted(false));
    }
    else {
        store.dispatch(ConversationActions.setIsConversationStarted(true));
    }
    store.dispatch(ConversationActions.updateRemainingTime(`${zeroPadding(Math.floor(remainingTime / 60))}:${zeroPadding(remainingTime % 60)}`));
};
const openModalToStartConversation = (conversation) => {
    if (checkUrl.isConversationPage() || checkUrl.isConversationEndPage()) {
        return;
    }
    store.dispatch(ModalActions.close());
    store.dispatch(ModalActions.setContents(React.createElement("div", null)));
    const path = `conversations/${conversation.id}`;
    if (isTeachers()) {
        store.dispatch(ModalActions.setContents(React.createElement(TeachersStartConversationModal, { conversation: Object.assign({}, conversation), path: path })));
    }
    else {
        store.dispatch(ModalActions.setContents(React.createElement(StudentsStartConversationModal, { conversation: Object.assign({}, conversation), path: path })));
    }
    store.dispatch(ModalActions.open());
};
export const moveToEvaluation = () => __awaiter(void 0, void 0, void 0, function* () {
    if (checkUrl.isConversationEndPage() || checkUrl.isConversationEvaluatePage()) {
        return;
    }
    const type = isTeachers() ? 'teachers' : isStudents() ? 'students' : null;
    const conversationId = store.getState().rootReducer.conversation.conversation.id;
    if (type) {
        const authToken = String(getAuthToken());
        const userId = isTeachers()
            ? store.getState().rootReducer.teachers.me.id
            : store.getState().rootReducer.students.me.id;
        if (!(authToken && userId)) {
            store.dispatch(push(resolvePath.page(type, 'signin')));
        }
        const hasEvaluation = yield hasEvaluationCheck(authToken, userId, conversationId);
        if (hasEvaluation) {
            store.dispatch(push(resolvePath.page(type, 'mypage')));
        }
        else {
            store.dispatch(push(resolvePath.page(type, `conversations/${conversationId}/evaluate`)));
        }
    }
});
const markConversationOpen = () => {
    store.dispatch(ConversationActions.setConversationOpen());
};
const showLateBalloon = () => {
    store.dispatch(ConversationActions.setYouAreLate());
    setTimeout(() => store.dispatch(ConversationActions.clearYouAreLate()), 5000);
};
const changeUser = (id) => __awaiter(void 0, void 0, void 0, function* () {
    const state = store.getState().rootReducer;
    const users = state.organizations.users;
    const designatedUser = users.find((user) => user.id === id);
    if (designatedUser) {
        yield store.dispatch(TeachersActions.getMe(designatedUser.auth_token));
        window.location.reload();
    }
});
const updateRemoteOnlineStatus = (online) => {
    store.dispatch(ConversationActions.setRemoteOnlineStatus(online));
};
const updateRemoteNetworkQuality = (networkQuality) => {
    store.dispatch(ConversationActions.setPartnerNetworkQuality(networkQuality));
};
const openModalToNotifyMatching = (conversation) => {
    const path = `reservations/detail/${conversation.id}`;
    store.dispatch(ModalActions.setContents(React.createElement(NotifyMatching, { conversation: conversation, path: path })));
    store.dispatch(ModalActions.open());
};
const updateMailAuthentication = () => {
    store.dispatch(updateMailAuthenticationSuccess());
    window.location.reload();
};
export const sendJoinedConversation = () => {
    const data = {
        command: 'message',
        data: '{"action":"joined_conversation"}',
        identifier: '{"channel":"ConversationsChannel"}',
    };
    send(data);
};
export const sendConversationsClose = () => {
    const data = {
        command: 'message',
        data: '{"action":"close"}',
        identifier: '{"channel":"ConversationsChannel"}',
    };
    send(data);
};
export const sendRemainingTime = () => {
    const data = {
        command: 'message',
        data: `{"action":"remaining_time"}`,
        identifier: '{"channel":"ConversationsChannel"}',
    };
    send(data);
};
export const sendRemoteOnline = (networkQuality) => {
    const data = {
        command: 'message',
        data: `{"action" : "remote_online", "networkQuality": "${networkQuality}"}`,
        identifier: '{"channel":"ConversationsChannel"}',
    };
    send(data);
};
